import { FC, useEffect, useRef, useState } from "react";
import { BrowserCodeReader, BrowserMultiFormatReader } from "@zxing/browser";
import { DecodeHintType } from "@zxing/library";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faX, faCameraRotate, faCamera } from "@fortawesome/free-solid-svg-icons";
import style from "./style.module.css";

interface props {
  onScan: (value: string) => void;
  onClose: () => void;
}

const BarcodeScannerModal: FC<props> = ({ onScan, onClose }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const readerRef = useRef<BrowserMultiFormatReader | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [retryMessage, setRetryMessage] = useState<string | null>(null);
  const [capturing, setCapturing] = useState(false);

  useEffect(() => {
    // TRY_HARDER makes 1D formats (UPC/EAN/Code128, the common retail barcodes)
    // significantly more reliable at the cost of a bit of CPU per decode.
    const hints = new Map();
    hints.set(DecodeHintType.TRY_HARDER, true);
    readerRef.current = new BrowserMultiFormatReader(hints);

    let cancelled = false;

    navigator.mediaDevices
      .getUserMedia({
        video: {
          facingMode: { ideal: "environment" },
          // Without an explicit resolution, browsers often default to a low
          // capture size (e.g. 640x480), which isn't sharp enough to reliably
          // decode a barcode unless the camera is held very close to it.
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          // Non-standard but supported on Chrome/Android — without it, some
          // phone cameras default to a focus range that's soft at the close
          // (macro-ish) distance barcode scanning needs. Ignored harmlessly
          // where unsupported (advanced constraints never throw).
          advanced: [{ focusMode: "continuous" } as MediaTrackConstraintSet],
        },
      })
      .then((stream) => {
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const name = err instanceof Error ? err.name : "";
        if (name === "NotAllowedError") {
          setError("Camera permission denied. Allow camera access to scan a barcode.");
        } else if (name === "NotFoundError") {
          setError("No camera found on this device.");
        } else {
          setError("Unable to start the camera.");
        }
      });

    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    };
  }, []);

  const handleClose = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    onClose();
  };

  const handleCapture = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const reader = readerRef.current;
    if (!video || !canvas || !reader) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    setCapturing(true);
    setRetryMessage(null);
    try {
      const binaryBitmap = BrowserCodeReader.createBinaryBitmapFromCanvas(canvas);
      const result = reader.decodeBitmap(binaryBitmap);
      onScan(result.getText());
    } catch {
      // decodeBitmap throws when no barcode is found in the captured frame —
      // let the user reposition and capture again, camera stays live.
      setRetryMessage("No barcode found in that photo — try again.");
    } finally {
      setCapturing(false);
    }
  };

  return (
    <div className={style.overlay}>
      <div className={style.modal}>
        <div className={style.header}>
          <h3>Scan Barcode</h3>
          <button type="button" className={style.closeButton} onClick={handleClose}>
            <FontAwesomeIcon icon={faX} />
          </button>
        </div>
        {error ? (
          <div className={style.errorBox}>
            <FontAwesomeIcon icon={faCameraRotate} />
            <p>{error}</p>
          </div>
        ) : (
          <>
            <div className={style.videoWrapper}>
              <video ref={videoRef} className={style.video} muted playsInline autoPlay />
              <div className={style.scanGuide} />
            </div>
            <canvas ref={canvasRef} className={style.hiddenCanvas} />
            <button
              type="button"
              className={style.captureButton}
              onClick={handleCapture}
              disabled={capturing}
            >
              <FontAwesomeIcon icon={faCamera} />
              {capturing ? "Scanning…" : "Tap to Scan"}
            </button>
            {retryMessage && <p className={style.retryMessage}>{retryMessage}</p>}
          </>
        )}
        {!error && !retryMessage && <p className={style.hint}>Position the barcode in view, then tap to scan</p>}
      </div>
    </div>
  );
};

export default BarcodeScannerModal;
